import { defineComponent as _defineComponent } from 'vue'
import type { Offer } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { UsePromotion } from '@/helpers/composables/usePromotion';

import PromotionRowCTA from '@/features/buybox/components/list/PromotionRowCTA.vue';
import PromotionRowOfferLabel from '@/features/buybox/components/list/PromotionRowOfferLabel.vue';
import PromotionRowOfferPresentation from '@/features/buybox/components/list/PromotionRowOfferPresentation.vue';
import {
	GetPackageProviderPlansDocument,
	GetPackageProviderPlansQuery,
} from '@/features/buybox/graphql/queries/GetPackageProviderPlans.query';
import { useGeoSwiper } from '@/features/buybox/composables/useGeoSwiper';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import { useQuery } from '@/helpers/composables/useApollo';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { OFFER_ENTITYID_KEY, getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { OfferLocation } from '@/helpers/offer-helper';
import { FALLBACK_PROMOTION_OFFER, primeImpactCountries } from '@/helpers/promotion-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import JwTVModal from '@/ui-components/video-player-modals/JwTVModal.vue';
import { computed, defineAsyncComponent, inject, ref } from 'vue';
import { useExperiment } from '@/libraries/experiment';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useI18n } from '@/helpers/composables/useI18n';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { useDoubleStackExp } from '@/components/experiments/DoubleStackExp';
import { useInjectAdServerIds } from '@/helpers/composables/useAdServer';
import ListPromotionRowMBRExp from '@/components/experiments/ExpBuyRent/components/ListPromotionRowMBRExp.vue';
import { useBuyRentExp } from '@/components/experiments/ExpBuyRent';
import { useSofterFreeTrialListBBExp } from '@/components/experiments/SoftFreeTrialListBB';
import { usePrimePlayBtnExp } from '@/components/experiments/ApplePlayBtnExp';
import { useAppleBingeExp } from '@/components/experiments/AppleBingeExp';

export type PlanOffer = ReturnType<typeof getPlanOffer>;

// EXP MERGE BUY RENT

export default /*#__PURE__*/_defineComponent({
  __name: 'ListPromotionRow',
  props: {
    title: null,
    offers: { default: () => [] },
    additionalContexts: { default: () => [] },
    hardcodedFallbackOfferId: null,
    hardcodedCountry: null,
    hardcodedShortName: null,
    hideRemoveAds: { type: Boolean },
    swiperIndex: null,
    largeFreeTrial: { type: Boolean }
  },
  emits: ["clickOut", "impressionCallback"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: UsePromotion['promotionOffer'];
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
	(e: 'impressionCallback'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: TitleDetail, offers: Offer[], additionalContexts: SnowplowContext[], hardcodedFallbackOfferId?: number, hardcodedCountry?: string | null, hardcodedShortName?: string, hideRemoveAds?: boolean, swiperIndex?: number, largeFreeTrial?: boolean };

const PromotionRowUISoftExp = defineAsyncComponent(
	() => import('@/components/experiments/SoftFreeTrialListBB/components/PromotionRowUISoftExp.vue')
);

const { activeVariantWithControlGroup: expMergeBuyRentVariant } = useExperiment('EXP_MERGE_BUY_RENT');
// EXP MERGE BUY RENT





const container = ref<HTMLElement | null>(null);
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const buyboxLocation = inject('offerLocation', OfferLocation.REGULAR_BUYBOX);
const noOffer = inject<boolean | undefined>('noOffer', undefined);
const { placement } = getClickoutTrackingPlacement();

const { country, language } = storeToRefs(useLanguageStore());
const { xServeTrack } = storeToRefs(useAppStore());
const adServerIds = useInjectAdServerIds();

const { setNoOfferTag } = useInternationalOffersStatus({ title: () => props.title });

const fetchGeoSwiperOffers = computed(() => !!props.hardcodedCountry && !!props.hardcodedShortName);

const { geoSwiperOffers } = useGeoSwiper({
	title: () => props.title,
	enable: fetchGeoSwiperOffers,
	offerShortName: () => props.hardcodedShortName,
	offerCountry: () => props.hardcodedCountry,
});

const offersForPromotion = computed(() => {
	if (fetchGeoSwiperOffers.value) {
		return geoSwiperOffers.value;
	}

	return props.offers;
});

const countryOrGeoCountry = computed(() => {
	return props.hardcodedCountry ?? country.value;
});

const { inPromotionCatalogue, promotionOffer, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: offersForPromotion,
	hardcodedShortName: () => props.hardcodedShortName,
	countryOrGeoCountry,
});

const { setDoubleStackTags } = useDoubleStackExp();
const { setBuyRentExpTags } = useBuyRentExp({ title: () => props.title });
const { setPrimePlayBtnParams } = usePrimePlayBtnExp();

const providerShortNames = computed(() =>
	fetchGeoSwiperOffers.value ? [props.hardcodedShortName] : [catalogueItem.value?.shortName ?? null].filter(Boolean)
);

const enableGetPackageProviderPlans = computed(
	() =>
		(catalogueItem.value?.placement && !!catalogueItem.value) ||
		(!catalogueItem.value?.placement && !inPromotionCatalogue.value) ||
		!!fetchGeoSwiperOffers.value
);

const { result: packageProviderPlans } = useQuery<GetPackageProviderPlansQuery>(
	GetPackageProviderPlansDocument,
	() => ({
		country: countryOrGeoCountry.value,
		language: language.value,
		packages: providerShortNames.value,
	}),
	{
		enabled: enableGetPackageProviderPlans,
		errorPolicy: 'all',
		prefetch: false,
	}
);

const jwRawEntityId = computed(() => episodeEntityId ?? props.title.id);
const { trackTagEvent, withArbitrageTracking, isArbitrating } = useArbitrage({ entityId: jwRawEntityId });

const crunchyRollUrl = computed(() => {
	const url = props.offers.find(offer => offer.package.packageId === 283)?.standardWebURL;
	if (!url) return 'http://crunchyroll.pxf.io/xk92Nv';

	return url.includes('crunchyroll.pxf.io') ? url : `http://crunchyroll.pxf.io/xk92Nv?u=${url}`;
});

const packagePlan = computed(() => {
	const mainPackage = inPromotionCatalogue.value
		? promotionOffer.value?.package
		: packageProviderPlans.value?.packages?.[0];

	if (!mainPackage) return null;

	return getPlanOffer(mainPackage);
});

const rowNumber = computed(() => {
	if (container.value == null) return undefined;

	const parentBuyBox = container.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(container.value));

	return index === -1 ? undefined : index;
});

// OVER_PROVIDER
// Bit of an odd case for the Promotion Placement to be a regular offer - I believe BI would like this for JWTV as well, so I think we will develop in this direction
// In this case over provider is halfway between a regular offer and a promotion offer, but they want to start tracking it as a regular offer
const { isOverProviderTitle } = useOverProviderExp({ promotionOffer });
const clickoutType = computed(() => (isOverProviderTitle.value ? 'regular' : 'free_trial'));
const offerLocation = computed(() => (isOverProviderTitle.value ? buyboxLocation : OfferLocation.FREE_TRIAL));
// OVER_PROVIDER

// Apple Binge Exp
const { activeVariantWithControlGroup: appleBingeExpVariant } = useExperiment('EXP_APPLE_BINGE');
const { setAppleBingeExpParams, appleBingeExpShortName } = useAppleBingeExp();

/** Applies `uct_country`, `rowNumber`, `offerLocation`. */
function clickoutBase(u: ClickoutUrl) {
	return u
		.set('uct_country', country.value)
		.addContexts(props.additionalContexts)
		.addContexts(titleContext.value)
		.setFreeTrial(!isOverProviderTitle.value)
		.setTitleSourceId(titleSourceId)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.setForTracking('offerLocation', offerLocation.value)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType.value)
		.updateContext('ClickoutContextGraphql', 'noOffer', noOffer)
		.updateContext('ClickoutContextGraphql', 'swiperIndex', props.swiperIndex)
		.setPageType(props.title.objectType)
		.setCanary(xServeTrack.value)
		.setAdIds(adServerIds.value)
		.setGenre(props.title.content.genres);
}

const hasAppleTvPlusOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 350));
const hasCrunchyRollOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 283));
const hasHuluOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 15));

const { setSofterFreeTrialListBBTags } = useSofterFreeTrialListBBExp();

const clickoutLink = computed(() => {
	if (!promotionOffer.value) return null;

	const packageId = promotionOffer.value?.package.packageId ?? 350;

	// // temporary bypass of uct for Crunchyroll
	if (packageId === 283) {
		return new ClickoutUrl({ ...promotionOffer.value, standardWebURL: crunchyRollUrl.value })
			.apply(clickoutBase)
			.apply(url => setDoubleStackTags(url, 'ft', promotionOffer.value?.package.shortName))
			.apply(url =>
				setBuyRentExpTags({
					url: url,
					type: 'ft',
					shortName: promotionOffer.value!.package.shortName,
					monetizationType: promotionOffer.value!.monetizationType,
				})
			);
		// EXP MERGE BUY RENT
	}

	if (isArbitrating.value && ['amp', 'atp'].includes(promotionOffer.value.package.shortName)) {
		const arbitrageUrl = withArbitrageTracking({
			offer: promotionOffer,
			title: () => props.title,
			placement: 'ft',
		});
		if (arbitrageUrl) {
			return arbitrageUrl.apply(clickoutBase).setGenre(props.title?.content.genres);
		}
	}

	const clickoutUrl = new ClickoutUrl(promotionOffer.value).apply(clickoutBase);

	// GEO_SWIPER
	if (props.hardcodedCountry) {
		if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
			const tag = props.hardcodedCountry === 'GB' ? 'amz-gb-ft-swiper-21' : 'amz-us-ft-swiper-20';

			const clickoutUrl = new ClickoutUrl(promotionOffer.value)
				.apply(clickoutBase)
				.set('uct_ctag', tag)
				.set('uct_country', props.hardcodedCountry);

			if (!inPromotionCatalogue.value) {
				clickoutUrl.set('uct_ct', 'fallback');
			}

			return clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
		} else {
			const tag = `${
				promotionOffer.value.package.shortName
			}-${props.hardcodedCountry.toLowerCase()}-ft-swiper-${country.value.toLowerCase()}`;
			clickoutUrl.set('uct_ctag', tag);
		}
	}
	// GEO_SWIPER

	if (promotionOffer.value?.package.packageId !== 350 && promotionOffer.value?.id === FALLBACK_PROMOTION_OFFER) {
		if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
			let offer = promotionOffer.value;
			const amazonPrimeUrl = new ClickoutUrl(offer).apply(clickoutBase).set('uct_ct', 'fallback');
			if (!primeImpactCountries.includes(country.value) && country.value !== 'GB') {
				amazonPrimeUrl.skipYieldOptimization();
			}

			// temporarily added: add a tag on no offer pages in certain countries, so we can compare them against international offer noOffer pages
			amazonPrimeUrl.apply(setNoOfferTag);

			// EXP MERGE BUY RENT
			amazonPrimeUrl.apply(url =>
				setBuyRentExpTags({
					url: url,
					type: 'ft',
					shortName: promotionOffer.value!.package.shortName,
					monetizationType: promotionOffer.value!.monetizationType,
				})
			);
			// EXP MERGE BUY RENT
			amazonPrimeUrl.apply(url => setSofterFreeTrialListBBTags(url, promotionOffer.value?.package.shortName));
			amazonPrimeUrl.apply(u => setPrimePlayBtnParams(u, 'ft', promotionOffer.value!.package.shortName));

			return amazonPrimeUrl.set(
				'r',
				amazonPrimeUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com')
			);
		}

		clickoutUrl.apply(url => setDoubleStackTags(url, 'ft', promotionOffer.value?.package.shortName));

		// EXP MERGE BUY RENT
		clickoutUrl.apply(url =>
			setBuyRentExpTags({
				url: url,
				type: 'ft',
				shortName: promotionOffer.value!.package.shortName,
				monetizationType: promotionOffer.value!.monetizationType,
			})
		);
		// EXP MERGE BUY RENT
		clickoutUrl.apply(url => setSofterFreeTrialListBBTags(url, promotionOffer.value?.package.shortName));
		clickoutUrl.apply(u => setPrimePlayBtnParams(u, 'ft', promotionOffer.value!.package.shortName));
		clickoutUrl.apply(u => setAppleBingeExpParams(u, promotionOffer.value!.package.shortName));

		return clickoutUrl;
	}

	const conversionTrackingByPackageId: Record<number, Record<'param' | 'value', string | null>> = {
		350: { value: hasAppleTvPlusOffer.value ? 'atds' : 'natds', param: 'ct' },
		283: { value: hasCrunchyRollOffer.value ? 'free_trial' : null, param: 'subId3' },
		15: { value: hasHuluOffer.value ? 'free_trial' : null, param: 'sub1' },
	};

	const partnerTokenByPackageId: Record<number, Record<'param' | 'value', string>> = {
		350: {
			value: 'justwatch_tv',
			param: 'itsct',
		},
	};

	if (partnerTokenByPackageId[packageId]?.value) {
		clickoutUrl.setOnOffer(partnerTokenByPackageId[packageId].param, partnerTokenByPackageId[packageId].value);
	}

	if (!!conversionTrackingByPackageId[packageId] && conversionTrackingByPackageId[packageId].param) {
		clickoutUrl.setOnOffer(
			conversionTrackingByPackageId[packageId].param!,
			conversionTrackingByPackageId[packageId].value
		);
	}

	if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
		clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	clickoutUrl.apply(url => setDoubleStackTags(url, 'ft', promotionOffer.value?.package.shortName));

	clickoutUrl.apply(url =>
		setBuyRentExpTags({
			url: url,
			type: 'ft',
			shortName: promotionOffer.value!.package.shortName,
			monetizationType: promotionOffer.value!.monetizationType,
		})
	);

	clickoutUrl.apply(url => setSofterFreeTrialListBBTags(url, promotionOffer.value?.package.shortName));

	clickoutUrl.apply(u => setPrimePlayBtnParams(u, 'ft', promotionOffer.value!.package.shortName));

	clickoutUrl.apply(u => setAppleBingeExpParams(u, promotionOffer.value!.package.shortName));

	return clickoutUrl;
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
	groupId: 'listPromotionRow',
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible || !clickoutLink.value) return;

	onIntersection(clickoutLink.value, isVisible);
}

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

const hasPromotedTag = computed(() => !inPromotionCatalogue.value);
// CTA has a border, and different text. Fallback only.
const isHighlighted = computed(
	() => !inPromotionCatalogue.value && (props.title.__typename === 'Show' || props.title.__typename === 'Season')
);
const isModalProvider = computed(() => promotionOffer.value?.package.shortName === 'jwt');

const { tc } = useI18n();
const freeTrialDetails = [
	tc('WEBAPP_PP_FT_EXP_DETAILS_1'),
	tc('WEBAPP_PP_FT_EXP_DETAILS_2'),
	tc('WEBAPP_PP_FT_EXP_DETAILS_3'),
];

function openModal() {
	if (!isModalProvider.value) return;
	if (clickoutLink.value) trackClickout(clickoutLink.value);

	ModalHelper.openModal(
		JwTVModal,
		{
			offer: promotionOffer.value,
			title: props.title,
			additionalContexts: getClickoutUrlContextsArray(clickoutLink.value),
		},
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	onClickOut();
}

function getPlanOffer(pckg: TitleOfferFragment['package'] | GetPackageProviderPlansQuery['packages'][0]) {
	if (pckg.planOffers.length > 0) {
		const plan = pckg.planOffers.find(offer => offer.isTrial || offer.children.length > 0);
		if (plan && plan.children.length > 0) {
			return { ...plan.children[0], retailPrice: plan.retailPrice };
		}
		return plan;
	}
	return null;
}

function onClickOut() {
	trackTagEvent(promotionOffer);

	emit('clickOut', {
		buyboxOffer: promotionOffer?.value,
		offerLocation: OfferLocation.FREE_TRIAL,
		row: container.value,
	});
}
// EXP_SOFTER_FREE_TRIAL_LIST_BB
const { activeVariant } = useExperiment('EXP_SOFTER_FREE_TRIAL_LIST_BB');

return { __sfc: true,PromotionRowUISoftExp, expMergeBuyRentVariant, props, emit, container, titleContext, filterOption, titleSourceId, episodeEntityId, buyboxLocation, noOffer, placement, country, language, xServeTrack, adServerIds, setNoOfferTag, fetchGeoSwiperOffers, geoSwiperOffers, offersForPromotion, countryOrGeoCountry, inPromotionCatalogue, promotionOffer, catalogueItem, setDoubleStackTags, setBuyRentExpTags, setPrimePlayBtnParams, providerShortNames, enableGetPackageProviderPlans, packageProviderPlans, jwRawEntityId, trackTagEvent, withArbitrageTracking, isArbitrating, crunchyRollUrl, packagePlan, rowNumber, isOverProviderTitle, clickoutType, offerLocation, appleBingeExpVariant, setAppleBingeExpParams, appleBingeExpShortName, clickoutBase, hasAppleTvPlusOffer, hasCrunchyRollOffer, hasHuluOffer, setSofterFreeTrialListBBTags, clickoutLink, onIntersection, onVisibilityChanged, observeVisibilityOptions, hasPromotedTag, isHighlighted, isModalProvider, tc, freeTrialDetails, openModal, getPlanOffer, onClickOut, activeVariant, PromotionRowCTA, PromotionRowOfferLabel, PromotionRowOfferPresentation, ClickoutAnchor, ProviderIcon, faCheck, ListPromotionRowMBRExp }
}

})